<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleReset">重置</button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          <span v-if="submitting">
            <i class="ri-loader-4-line spin"></i>
            儲存中...
          </span>
          <span v-else>
            <i class="ri-save-line"></i>
            儲存設定
          </span>
        </button>
      </div>
    </div>

    <div class="page-container">

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else class="card">
      <div class="card-body">
        <form @submit.prevent="handleSubmit">
          <div class="form-section">
            <h2 class="section-title">過期規則</h2>
            <p class="section-hint">
              以下規則會自動套用於所有發點方式（訂單回饋、批次發放、手動發放、活動獎勵等），無須每次指定到期日。
            </p>
            
            <div class="form-group expiry-type-group">
              <div id="expiry-type-label" class="form-label">
                過期類型 <span class="required">*</span>
              </div>
              <div
                class="expiry-type-grid"
                role="radiogroup"
                aria-labelledby="expiry-type-label"
              >
                <button
                  v-for="opt in expiryTypeOptions"
                  :key="opt.value"
                  type="button"
                  class="expiry-type-card"
                  :class="{ active: form.expiryType === opt.value }"
                  :aria-pressed="form.expiryType === opt.value"
                  @click="selectExpiryType(opt.value)"
                >
                  <i :class="opt.icon" aria-hidden="true"></i>
                  <span class="expiry-type-card-title">{{ opt.label }}</span>
                  <span class="expiry-type-card-desc">{{ opt.desc }}</span>
                </button>
              </div>
              <span v-if="errors.expiryType" class="error-text">{{ errors.expiryType }}</span>
            </div>

            <div v-if="form.expiryType === 'fixed_days'" class="form-group">
              <label class="form-label">有效天數 <span class="required">*</span></label>
              <input
                v-model.number="form.expiryDays"
                type="number"
                class="form-control"
                placeholder="點數生效日起算 N 天後到期"
                min="1"
                required
              />
              <span v-if="errors.expiryDays" class="error-text">{{ errors.expiryDays }}</span>
            </div>

            <div v-if="form.expiryType === 'rolling'" class="form-group">
              <label class="form-label">
                <input
                  v-model="form.rollingEnabled"
                  type="checkbox"
                />
                啟用滾動過期
              </label>
              <div v-if="form.rollingEnabled" class="form-group" style="margin-top: 10px;">
                <label class="form-label">滾動天數</label>
                <input
                  v-model.number="form.rollingDays"
                  type="number"
                  class="form-control"
                  placeholder="每次獲得或折抵點數後，自當天起至少保留 N 天"
                  min="1"
                />
              </div>
              <small class="form-text">會員每次獲得或折抵點數時，系統會將尚未到期之點數到期日延後（取較晚者）。</small>
            </div>

            <div v-if="form.expiryType === 'fixed_date'" class="form-group">
              <label class="form-label">每年結算日（月／日）</label>
              <input
                v-model="form.fixedExpiryDate"
                type="date"
                class="form-control"
              />
              <small class="form-text">僅使用「月、日」：該年度內發放的點數，於下一個此月此日到期（與西元年份無關）。</small>
            </div>

            <div v-if="form.expiryType === 'tier_based'" class="form-group tier-rules-block">
              <label class="form-label">各等級有效天數 <span class="required">*</span></label>
              <p class="form-text" style="margin-bottom: 12px;">
                點數依會員<strong>等級代碼</strong>套用天數：自點數<strong>生效日</strong>起算，滿 N 天後到期。
                可只填「預設」作為未單獨設定的等級之後備；或為每個等級都填寫（建議）。
              </p>
              <div v-if="tierRowsLoading" class="loading-inline">載入等級列表中…</div>
              <div v-else class="tier-rules-table-wrap">
                <table class="tier-rules-table">
                  <thead>
                    <tr>
                      <th>等級名稱</th>
                      <th>等級代碼</th>
                      <th style="width: 200px;">有效天數</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, idx) in tierRows" :key="row.tierCode + '-' + idx">
                      <td>
                        <span :class="{ 'tier-default-label': row.isDefault }">{{ row.tierName }}</span>
                      </td>
                      <td>
                        <code v-if="!row.isDefault" class="tier-code">{{ row.tierCode }}</code>
                        <span v-else class="form-text">default</span>
                      </td>
                      <td>
                        <input
                          v-model.number="row.expiryDays"
                          type="number"
                          class="form-control"
                          min="1"
                          :placeholder="row.isDefault ? '後備天數（選填）' : '必填或改填預設'"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <span v-if="errors.tierRules" class="error-text">{{ errors.tierRules }}</span>
              <small v-if="!tierRowsLoading && tierRows.length <= 1" class="form-text">
                尚未建立啟用中的會員等級時，請至少設定「預設」天數，或至「會員等級管理」新增等級。
              </small>
            </div>
          </div>

          <div class="form-section">
            <h2 class="section-title">提醒設定</h2>
            
            <div class="form-group">
              <div class="toggle-row">
                <span class="toggle-label">啟用過期提醒</span>
                <label class="toggle-switch">
                  <input v-model="form.reminderEnabled" type="checkbox" />
                  <span class="toggle-slider"></span>
                </label>
                <span class="toggle-status" :class="form.reminderEnabled ? 'on' : 'off'">
                  {{ form.reminderEnabled ? '已啟用' : '已停用' }}
                </span>
              </div>
            </div>

            <div v-if="form.reminderEnabled" class="form-group">
              <label class="form-label">提醒天數</label>
              <input
                v-model="reminderDaysText"
                type="text"
                class="form-control"
                placeholder="例如：30,7,1（過期前 30 天、7 天、1 天提醒）"
              />
              <small class="form-text">多個天數以逗號分隔</small>
            </div>

            <div v-if="form.reminderEnabled" class="form-group reminder-notif-block">
              <label class="form-label">提醒管道與範本</label>
              <NotificationChannelSelector
                v-model:channels="form.reminderChannels"
                v-model:template-ids="form.reminderTemplateIds"
                show-default-template-preview
                template-preview-preset="points_expiry"
              />
              <span v-if="errors.reminderChannels" class="error-text" style="display:block;margin-top:8px;">
                {{ errors.reminderChannels }}
              </span>
            </div>
          </div>

        </form>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { pointsService } from '@/api/modules/points.js';
import { memberTierService } from '@/api/modules/memberTiers.js';
import { showSuccess, showError } from '@/utils/swal.js';
import NotificationChannelSelector from '@/components/NotificationChannelSelector.vue';

export default {
  name: 'PointsExpiryConfig',
  components: { NotificationChannelSelector },
  setup() {
    const { pageTitle } = usePageTitle('點數過期設定');
    const loading = ref(false);
    const submitting = ref(false);
    
    const form = reactive({
      expiryType: 'never',
      expiryDays: null,
      rollingEnabled: false,
      rollingDays: null,
      fixedExpiryDate: null,
      tierRules: null,
      reminderEnabled: true,
      reminderDays: [30, 7, 1],
      reminderChannels: ['line'],
      reminderTemplateIds: {}
    });

    const reminderDaysText = ref('30,7,1');
    const tierRows = ref([]);
    const tierRowsLoading = ref(false);

    const errors = reactive({});

    const expiryTypeOptions = [
      {
        value: 'never',
        label: '永不過期',
        desc: '點數無到期日',
        icon: 'ri-infinity-line',
      },
      {
        value: 'fixed_days',
        label: '固定到期',
        desc: '發點後 N 天到期',
        icon: 'ri-calendar-todo-line',
      },
      {
        value: 'rolling',
        label: '滾動到期',
        desc: '有獲點／折抵則延長',
        icon: 'ri-refresh-line',
      },
      {
        value: 'fixed_date',
        label: '年度清零',
        desc: '每年固定月日結算',
        icon: 'ri-calendar-event-line',
      },
      {
        value: 'tier_based',
        label: '依會員等級',
        desc: '各等級不同天數',
        icon: 'ri-vip-crown-line',
      },
    ];

    const selectExpiryType = (type) => {
      if (form.expiryType === type) return;
      form.expiryType = type;
      handleExpiryTypeChange();
    };

    /**
     * 依 API 等級與已儲存 tierRules 建立表單列
     */
    const loadTierRows = async (savedRules) => {
      const rules =
        savedRules && typeof savedRules === 'object' && !Array.isArray(savedRules) ? savedRules : {};
      tierRowsLoading.value = true;
      try {
        const res = await memberTierService.getTiers({ isActive: true });
        const raw = res?.data;
        const items = Array.isArray(raw) ? raw : [];
        const sorted = [...items].sort(
          (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
        );
        const rows = sorted.map((t) => ({
          tierCode: t.tierCode,
          tierName: t.tierName || t.tierCode,
          expiryDays:
            rules[t.tierCode]?.expiryDays != null && rules[t.tierCode]?.expiryDays !== ''
              ? Number(rules[t.tierCode].expiryDays)
              : null,
          isDefault: false,
        }));

        Object.keys(rules).forEach((code) => {
          if (code === 'default' || code === 'regular') return;
          if (!rows.some((r) => r.tierCode === code)) {
            rows.push({
              tierCode: code,
              tierName: `${code}（已停用或已刪除）`,
              expiryDays:
                rules[code]?.expiryDays != null ? Number(rules[code].expiryDays) : null,
              isDefault: false,
              orphan: true,
            });
          }
        });

        rows.push({
          tierCode: 'default',
          tierName: '預設（後備）',
          expiryDays:
            rules.default?.expiryDays != null && rules.default?.expiryDays !== ''
              ? Number(rules.default.expiryDays)
              : null,
          isDefault: true,
        });

        tierRows.value = rows;
      } catch {
        tierRows.value = [
          {
            tierCode: 'default',
            tierName: '預設（無法載入等級列表時請填此欄）',
            expiryDays:
              rules.default?.expiryDays != null ? Number(rules.default.expiryDays) : null,
            isDefault: true,
          },
        ];
      } finally {
        tierRowsLoading.value = false;
      }
    };

    const buildTierRulesFromRows = () => {
      const tierRules = {};
      for (const row of tierRows.value) {
        const d = row.expiryDays;
        if (d !== '' && d != null && !Number.isNaN(Number(d)) && Number(d) >= 1) {
          tierRules[row.tierCode] = { expiryDays: Number(d) };
        }
      }
      return tierRules;
    };

    const loadConfig = async () => {
      loading.value = true;
      try {
        const response = await pointsService.getExpiryConfig();
        if (response.success && response.data) {
          Object.assign(form, response.data);
          if (
            !form.reminderTemplateIds ||
            typeof form.reminderTemplateIds !== 'object' ||
            Array.isArray(form.reminderTemplateIds)
          ) {
            form.reminderTemplateIds = {};
          }
          if (response.data.reminderDays) {
            reminderDaysText.value = response.data.reminderDays.join(',');
          }
          if (response.data.expiryType === 'tier_based') {
            await loadTierRows(response.data.tierRules);
          }
        }
      } catch (error) {
        showError('載入設定失敗', error.message);
      } finally {
        loading.value = false;
      }
    };

    const handleExpiryTypeChange = () => {
      // 重置相關欄位
      form.expiryDays = null;
      form.rollingEnabled = false;
      form.rollingDays = null;
      form.fixedExpiryDate = null;
      form.tierRules = null;
      errors.tierRules = '';
      if (form.expiryType === 'tier_based') {
        loadTierRows(null);
      }
    };

    const validateForm = () => {
      errors.expiryType = '';
      errors.expiryDays = '';
      errors.tierRules = '';
      errors.reminderChannels = '';

      if (!form.expiryType) {
        errors.expiryType = '請選擇過期類型';
        return false;
      }

      if (form.expiryType === 'fixed_days' && (!form.expiryDays || form.expiryDays <= 0)) {
        errors.expiryDays = '請輸入有效的過期天數';
        return false;
      }

      if (form.expiryType === 'tier_based') {
        const nonDef = tierRows.value.filter((r) => !r.isDefault);
        const defRow = tierRows.value.find((r) => r.isDefault);
        const defOk = defRow && Number(defRow.expiryDays) >= 1;
        const allNonDefOk =
          nonDef.length > 0 && nonDef.every((r) => Number(r.expiryDays) >= 1);
        if (!defOk && !allNonDefOk) {
          errors.tierRules =
            '請為「每個啟用等級」填寫有效天數（≥1），或至少填寫「預設（後備）」天數';
          return false;
        }
      }

      if (
        form.reminderEnabled &&
        (!form.reminderChannels || form.reminderChannels.length === 0)
      ) {
        errors.reminderChannels = '啟用提醒時請至少選擇一個提醒管道';
        return false;
      }

      return true;
    };

    const handleSubmit = async () => {
      if (!validateForm()) {
        return;
      }

      submitting.value = true;
      try {
        const data = {
          expiryType: form.expiryType,
          expiryDays: form.expiryType === 'fixed_days' ? form.expiryDays : null,
          rollingEnabled: form.expiryType === 'rolling' ? form.rollingEnabled : false,
          rollingDays: form.expiryType === 'rolling' && form.rollingEnabled ? form.rollingDays : null,
          fixedExpiryDate: form.expiryType === 'fixed_date' ? form.fixedExpiryDate : null,
          tierRules:
            form.expiryType === 'tier_based' ? buildTierRulesFromRows() : null,
          reminderEnabled: form.reminderEnabled,
          reminderDays: form.reminderEnabled && reminderDaysText.value
            ? reminderDaysText.value.split(',').map(d => parseInt(d.trim())).filter(d => !isNaN(d))
            : [],
          reminderChannels: form.reminderEnabled ? form.reminderChannels : [],
          reminderTemplateIds:
            form.reminderEnabled && form.reminderTemplateIds && typeof form.reminderTemplateIds === 'object'
              ? form.reminderTemplateIds
              : {}
        };

        const response = await pointsService.updateExpiryConfig(data);

        if (response.success) {
          await showSuccess('設定儲存成功', '點數過期規則已更新');
          await loadConfig();
        }
      } catch (error) {
        showError('儲存設定失敗', error.message || '請檢查輸入資料');
      } finally {
        submitting.value = false;
      }
    };

    const handleReset = () => {
      loadConfig();
    };

    onMounted(() => {
      loadConfig();
    });

    return {
      pageTitle,
      loading,
      form,
      tierRows,
      tierRowsLoading,
      reminderDaysText,
      errors,
      submitting,
      handleExpiryTypeChange,
      expiryTypeOptions,
      selectExpiryType,
      handleSubmit,
      handleReset
    };
  }
};
</script>

<style scoped>
.form-section {
  margin-bottom: 30px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
}

.section-hint {
  font-size: 14px;
  color: #5a6c7d;
  line-height: 1.5;
  margin: -8px 0 20px;
}

.expiry-type-group .form-label {
  margin-bottom: 10px;
}

.expiry-type-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(158px, 1fr));
  gap: 12px;
}

.expiry-type-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  padding: 14px 14px 12px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background 0.15s ease;
  font-family: inherit;
  color: #1e293b;
}

.expiry-type-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);
}

.expiry-type-card:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

.expiry-type-card.active {
  border-color: #3b82f6;
  background: linear-gradient(165deg, #eff6ff 0%, #ffffff 55%);
  box-shadow: 0 4px 14px rgba(59, 130, 246, 0.14);
}

.expiry-type-card i {
  font-size: 24px;
  color: #64748b;
  margin-bottom: 8px;
  line-height: 1;
}

.expiry-type-card.active i {
  color: #2563eb;
}

.expiry-type-card-title {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  margin-bottom: 4px;
}

.expiry-type-card-desc {
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

@media (max-width: 520px) {
  .expiry-type-grid {
    grid-template-columns: 1fr;
  }
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.required {
  color: #dc3545;
}

.error-text {
  display: block;
  color: #dc3545;
  font-size: 14px;
  margin-top: 5px;
}

.form-text {
  display: block;
  margin-top: 5px;
  color: #6c757d;
  font-size: 14px;
}

.checkbox-group {
  display: flex;
  gap: 20px;
  margin-top: 10px;
}

.checkbox-group label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: normal;
  cursor: pointer;
}

.reminder-notif-block {
  max-width: 920px;
}

.toggle-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle-label {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #ccc;
  border-radius: 24px;
  transition: background 0.2s;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  top: 3px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.toggle-switch input:checked + .toggle-slider {
  background: #4caf50;
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(20px);
}

.toggle-status {
  font-size: 13px;
  font-weight: 500;
}

.toggle-status.on {
  color: #4caf50;
}

.toggle-status.off {
  color: #9ca3af;
}
.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.tier-rules-block {
  max-width: 720px;
}

.tier-rules-table-wrap {
  overflow-x: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.tier-rules-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.tier-rules-table th,
.tier-rules-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.tier-rules-table th {
  background: #f8f9fa;
  font-weight: 600;
}

.tier-rules-table tr:last-child td {
  border-bottom: none;
}

.tier-code {
  font-size: 13px;
  background: #f1f3f5;
  padding: 2px 8px;
  border-radius: 4px;
}

.tier-default-label {
  font-weight: 600;
  color: #495057;
}

.loading-inline {
  padding: 12px;
  color: #6c757d;
  font-size: 14px;
}
</style>











